import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { Rate } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import NoImage from "../../../images/no-image.jpg"; // replace with your placeholder image
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getCountries } from "../../../services/CountryService";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import { Button } from "react-bootstrap";
import {
  createBlog,
  getBlogById,
  updateBlog,
} from "../../../services/BlogService";
import CkEditorBlog from "../Forms/CkEditor/CkEditorBlog";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import CustomClassicEditor from "../CustomClassicEditor";

const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

const multiSelectComponents = {
  MultiValue: ({ data, removeProps }) => (
    <div
      className="d-flex align-items-center"
      style={{
        background: "#EEF2FF",
        borderRadius: "16px",
        padding: "2px 8px",
        margin: "2px",
        maxWidth: "180px",
      }}
    >
      <span
        style={{
          fontSize: "12px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {data.label}
      </span>

      <span
        {...removeProps}
        style={{
          marginLeft: "6px",
          cursor: "pointer",
          fontWeight: "bold",
        }}
      >
        ×
      </span>
    </div>
  ),
};

const AddBlog = () => {
  const [formData, setFormData] = useState({
    title: "",
    desc: "",
    seo_title: "",
    seo_desc: "",
    country_ids: null,
    status: false,
    show_on_home: false,
    blog_status: statusOptions[0],
    blog_published_on: null,
  });

  const [blogImage, setBlogImage] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [errors, setErrors] = useState({});
  const hasFetched = useRef(false);
  const hasDataFetched = useRef(false);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // dropdown mock data
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryPage, setCountryPage] = useState(1);
  const [countryTotalPages, setCountryTotalPages] = useState(1);
  const [countryLoading, setCountryLoading] = useState(false);

  const fetchCountry = async (pageNumber = 1, query = "") => {
    if (pageNumber > countryTotalPages) return; // no more pages
    try {
      setCountryLoading(true);
      const limit = 5; // set per-page limit
      const response = await getCountries(pageNumber, limit, query);
      const { data, pagination } = response.data;

      const options = (data || []).map((c) => ({ value: c.id, label: c.name }));

      if (pageNumber === 1) {
        // first page or search => replace
        setCountryOptions(options);
      } else {
        // append for scroll
        setCountryOptions((prev) => [...prev, ...options]);
      }

      setCountryPage(pagination?.page || pageNumber);
      setCountryTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setCountryLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileChange = (e) => {
    setBlogImage(e.target.files[0]);
  };

  const handleCountryChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      country_ids: selectedOption,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!id && !blogImage) newErrors.blog_image = "Blog image is required";
    if (!formData.title.trim()) newErrors.title = "Blog title is required";
    if (!formData.desc.trim()) newErrors.desc = "Blog content is required";
    if (!formData.seo_title.trim())
      newErrors.seo_title = "SEO title is required";
    if (!formData.seo_desc.trim())
      newErrors.seo_desc = "SEO description is required";

    if (!formData.country_ids) newErrors.country = "Country is required";

    if (formData.blog_status == "published" && !formData.blog_published_on) {
      newErrors.blog_published_on = "Published date is required";
    }

    return newErrors;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // 2️⃣ Then fetch blog by ID
        if (id) {
          const res = await getBlogById(id);
          if (res.data?.success) {
            const blog = res.data.data;

            const selectedCountries =
              (blog.country_ids || [])
                .map((id) => {
                  const country = countryOptions.find((c) => c.value === id);
                  return country
                    ? country
                    : { value: id, label: `Country ${id}` }; // fallback label
                })
                .filter(Boolean) || [];

            setFormData({
              title: blog.title || "",
              desc: blog.desc || "",
              seo_title: blog.seo_title || "",
              seo_desc: blog.seo_desc || "",
              country_ids: selectedCountries,
              status: blog.status || false,
              show_on_home: blog.show_on_home || false,
              blog_status:
                statusOptions.find((opt) => opt.value === blog.blog_status) ||
                statusOptions[0],
              blog_published_on: blog.blog_published_on
                ? new Date(blog.blog_published_on).toISOString().slice(0, 16)
                : "",
            });
            setExistingImage(blog.blog_image_url || "");
          } else {
            console.error("Failed to fetch bank by ID");
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchCountry();
      fetchData();
    }
  }, [id]);

  const cleanEditorContent = (html = "") => {
    if (!html) return "";

    let cleaned = html.trim();

    // loop because last may be [empty_line] + <p>&nbsp;</p> + [empty_line]
    let changed = true;

    while (changed) {
      changed = false;

      // remove only one trailing empty paragraph block
      const removedParagraph = cleaned.replace(
        /<p>(?:&nbsp;|\s|<br\s*\/?>)*<\/p>\s*$/i,
        ""
      );

      if (removedParagraph !== cleaned) {
        cleaned = removedParagraph.trim();
        changed = true;
      }

      // remove only one trailing [empty_line]
      const removedToken = cleaned.replace(
        /\[empty_line\]\s*$/i,
        ""
      );

      if (removedToken !== cleaned) {
        cleaned = removedToken.trim();
        changed = true;
      }
    }

    return cleaned;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      console.log("Validation Errors:", validationErrors);
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const fd = new FormData();
      if (blogImage) fd.append("blog_image", blogImage);

      // append normal fields
      const submitData = {
        ...formData,
        title: cleanEditorContent(formData.title),
        desc: cleanEditorContent(formData.desc),
        show_on_home: !!formData.show_on_home,
        status: !!formData.status,
        country_ids: formData.country_ids?.map((c) => c.value) || [],
      };

      // append normal fields
      Object.entries(submitData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // ✅ For arrays (like country_ids)
          fd.append(key, JSON.stringify(value));
        } else if (typeof value === "object" && value !== null) {
          // ✅ For objects (like blog_status from react-select)
          if (key === "blog_status") {
            fd.append(key, value.value); // send as "draft" or "published"
          } else {
            fd.append(key, JSON.stringify(value)); // fallback for any future object
          }
        } else {
          // ✅ For simple strings, booleans, etc.
          fd.append(key, value);
        }
      });

      if (id) {
        const response = await updateBlog(id, fd);
        if (response.data?.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text:
              response.data?.message || "Blog details updated successfully!",
          });
          navigate("/blog",
            {
              state: {
                page: location.state?.page || 1,
                searchTerm: location.state?.searchTerm || "",
              },
            }
          );
        } else {
          console.error(
            "Error updating blog:",
            response?.data?.message || "Unknown error"
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response?.data?.message || "Failed to update blog",
          });
        }
      } else {
        const response = await createBlog(fd);
        if (response.data?.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: response.data?.message || "Blog details added successfully",
          });
          navigate("/blog",
            {
              state: {
                page: location.state?.page || 1,
                searchTerm: location.state?.searchTerm || "",
              },
            }
          );
        } else {
          console.error(
            "Error updating blog:",
            response.data?.message || "Unknown error"
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data?.message || "Failed to added blog",
          });
        }
      }
    } catch (err) {
      console.error(
        "Error while adding or updating blog:",
        err || "Unknown error"
      );
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err || "Failed to adding or updating blog",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        activeMenu={`${id ? "Edit Blog" : "Add Blog"}`}
        motherMenu="Blog"
        activeMenuLink={`${id ? `edit-blog/${id}` : "add-blog"}`}
        motherMenuLink={"blog"}
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-12 col-xxl-12">
              <div className="card h-auto">
                <div className="card-body">
                  <form onSubmit={handleSubmit} encType="multipart/form-data">
                    {/* Blog Image */}
                    <div
                      onClick={() => {
                        navigate("/blog",
                          {
                            state: {
                              page: location.state?.page || 1,
                              searchTerm: location.state?.searchTerm || "",
                            },
                          }
                        );
                      }}
                      className="mb-4 d-flex align-items-center flex-wrap gap-2 cursor-pointer w-max"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <div>Blog List</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Blog Image</label>
                      <div>
                        <img
                          src={
                            blogImage
                              ? URL.createObjectURL(blogImage)
                              : existingImage || NoImage
                          }
                          alt="Blog Image"
                          width={100}
                          height={100}
                          className="rounded border mb-2"
                        />
                      </div>
                      <div className="change-btn d-flex align-items-center flex-wrap">
                        <input
                          type="file"
                          onChange={handleFileChange}
                          id="imageUpload"
                          className="d-none"
                        />
                        <label
                          htmlFor="imageUpload"
                          className="btn btn-primary ms-0"
                        >
                          Select Image
                        </label>
                      </div>
                      {errors.blog_image && (
                        <div className="text-danger mt-1" id="blog_image_error">
                          {errors.blog_image}
                        </div>
                      )}
                    </div>

                    {/* Blog Title */}
                    <div className="mb-3">
                      <label className="form-label">Blog Title</label>
                      <CKEditor
                        editor={CustomClassicEditor}
                        data={(formData.title).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                        onChange={(event, editor) => {
                          setFormData({
                            ...formData,
                            title: editor.getData(),
                          });
                        }}
                      />
                      {/* <CkEditorBlog
                        value={formData.title || ""}
                        onChange={(val) =>
                          setFormData({ ...formData, title: val })
                        }
                        error={errors.title}
                      /> */}
                      {errors.title && (
                        <div className="invalid-feedback">{errors.title}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Blog Description</label>
                      <CKEditor
                        editor={CustomClassicEditor}
                        data={(formData.desc).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                        onChange={(event, editor) => {
                          setFormData({
                            ...formData,
                            desc: editor.getData(),
                          });
                        }}
                      />
                      {/* <CkEditorBlog
                        value={formData.desc || ""}
                        onChange={(val) =>
                          setFormData({ ...formData, desc: val })
                        }
                        error={errors.desc}
                      /> */}
                      {errors.desc && (
                        <div className="invalid-feedback">{errors.desc}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Blog SEO Title</label>
                      <input
                        type="text"
                        name="seo_title"
                        className={`form-control ${errors.seo_title ? "is-invalid" : ""
                          }`}
                        value={formData.seo_title}
                        onChange={handleChange}
                        placeholder="Enter blog SEO title"
                      />
                      {errors.seo_title && (
                        <div className="invalid-feedback">
                          {errors.seo_title}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Blog SEO Description</label>
                      <textarea
                        name="seo_desc"
                        className={`form-control ${errors.seo_desc ? "is-invalid" : ""
                          }`}
                        rows={2}
                        value={formData.seo_desc}
                        onChange={handleChange}
                        placeholder="Enter blog SEO description"
                      ></textarea>
                      {errors.seo_desc && (
                        <div className="invalid-feedback">
                          {errors.seo_desc}
                        </div>
                      )}
                    </div>

                    {/* Open Account Link Checkbox + URL */}
                    <div className="mb-3">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="status"
                          checked={formData.status}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              status: e.target.checked,
                            })
                          }
                        />
                        <label className="form-check-label" htmlFor="status">
                          Show Blog
                        </label>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="show_on_home"
                          checked={formData.show_on_home}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              show_on_home: e.target.checked,
                            })
                          }
                        />
                        <label className="form-check-label" htmlFor="show_on_home">
                          Show On home
                        </label>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Blog status</label>
                      <Select
                        value={formData.blog_status}
                        onChange={(value) =>
                          handleSelectChange("blog_status", value)
                        }
                        options={statusOptions}
                        placeholder="Select blog status"
                        closeMenuOnSelect={true}
                        menuPlacement="auto"
                        menuPosition="fixed"
                      />
                      {errors.blog_status && (
                        <div className="text-danger mt-1">
                          {errors.blog_status}
                        </div>
                      )}
                    </div>

                    {formData.blog_status?.value == "draft" && (
                      <div className="mb-3">
                        <label className="form-label">Blog Publish Date</label>
                        <div className="input-hasicon mb-sm-0 mb-3">
                          <input
                            type="datetime-local"
                            name="datepicker"
                            id="datepicker"
                            className=" form-control mb-xl-0 mb-3"
                            value={formData.blog_published_on || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                blog_published_on: e.target.value,
                              })
                            }
                            placeholder="Select publish date"
                          />
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">Country</label>
                      <Select
                        isMulti
                        value={formData.country_ids}
                        onChange={handleCountryChange}
                        options={countryOptions}
                        placeholder={
                          countryLoading
                            ? "Loading countries..."
                            : "Select country"
                        }
                        isLoading={countryLoading}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        onMenuScrollToBottom={() =>
                          fetchCountry(countryPage + 1)
                        }
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        isClearable={false}
                        isSearchable={false}
                        components={multiSelectComponents}
                      />
                      {errors.country && (
                        <div className="text-danger mt-1">{errors.country}</div>
                      )}
                    </div>

                    <hr style={{ margin: "0px" }} className="mx-2" />
                    <div className="card-footer border-0 text-end py-3 d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          navigate("/blog", {
                            state: {
                              page: location.state?.page || 1,
                              searchTerm: location.state?.searchTerm || "",
                            },
                          })
                        }}
                      >
                        Back
                      </Button>

                      {/* Save / Update Button */}
                      <Button type="submit" className="btn btn-primary btn-sm">
                        {id ? "Update" : "Save"}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddBlog;
